// src/features/patient/dashboard/api/recentLabsApi.ts

/**
 * Purpose : The dashboard's "Most Recent Lab Result" widget, ready to render
 * (GET /api/patient/dashboard/recent-labs). The backend decides what a lab
 * result is (never an exam finding or a vital sign), the latest draw date,
 * the score's counts and the two results to highlight — nothing is worked
 * out from raw Observations here.
 *
 * Contents :
 *
 * * type(RecentLabStatus) : a highlighted result's status
 * * type(RecentLabHighlight) : one highlighted result
 * * type(RecentLabs) : the widget's data
 * * function(getRecentLabs) : fetch it for the viewed profile
 */

import { apiFetch } from "@/lib/apiClient";

// type(RecentLabStatus)
export type RecentLabStatus = "critical_high" | "critical_low" | "high" | "low" | "abnormal" | "normal" | "unknown";

// type(RecentLabHighlight)
export type RecentLabHighlight = {
  id: string;
  /** The record to open (a resolved EMPI cluster's golden row). */
  recordId: string;
  label: string;
  /** A number, or a short qualitative result ("Negative"). */
  value: string;
  valueType: "numeric" | "qualitative";
  unit: string;
  status: RecentLabStatus;
  /** "70-100 mg/dL", "" when there's no range. */
  rangeLabel: string;
  /** YYYY-MM-DD */
  effectiveDate: string;
};

// type(RecentLabs)
export type RecentLabs = {
  /** YYYY-MM-DD of the most recent lab draw; null = no lab results at all. */
  date: string | null;
  /** Lab results with a real result on that date. */
  total: number;
  /** Those with a range or interpretation to judge them by. */
  assessed: number;
  /** Those judged normal: the score is inRange / assessed. */
  inRange: number;
  /** 0–2, abnormal first. */
  highlights: RecentLabHighlight[];
};

// function(getRecentLabs)
/**
 * Fetch the viewed profile's most recent lab results summary.
 *
 * @param actingAsProfileId - The dependent being viewed, or undefined for the patient themselves.
 * @returns The widget's data.
 */
export async function getRecentLabs(actingAsProfileId?: string): Promise<RecentLabs> {
  const headers: Record<string, string> = actingAsProfileId ? { "X-Acting-As-Profile-Id": actingAsProfileId } : {};
  const { data, error } = await apiFetch<RecentLabs>("/api/patient/dashboard/recent-labs", {
    method: "GET",
    portal: "patient",
    headers,
  });

  if (error || !data) {
    throw new Error(error?.message || "Couldn't load your recent lab results.");
  }

  return data;
}
